import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  getOutboxEvents,
  retryOutboxEvent,
  getOutboxHealth,
  getOutboxTypeStats,
  pauseOutboxDispatcher,
  resumeOutboxDispatcher,
} from "api/outboxApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /admin/outbox — operator view of the transactional outbox. Surfaces failed,
 * pending, and processed events with last_error / retry_count so DLQ'd
 * notifications and other side effects don't disappear silently.
 */

const STATUS_COLOR = {
  pending: "#0ea5e9",
  processed: "#16a34a",
  failed: "#dc2626",
};

const fmt = (ms) => (ms ? new Date(ms).toLocaleString() : "—");

export const OutboxAdminPage = () => {
  const [rows, setRows] = useState([]);
  const [health, setHealth] = useState(null);
  const [typeStats, setTypeStats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [evRes, hRes, tsRes] = await Promise.all([
        getOutboxEvents(statusFilter || undefined),
        getOutboxHealth(),
        getOutboxTypeStats(),
      ]);
      setRows(unwrapList(evRes));
      setHealth(unwrapObject(hRes));
      setTypeStats(unwrapList(tsRes));
    } catch (e) {
      setError(e?.message || "Failed to load outbox events");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.type || ""} ${r.aggregateType || ""} ${r.aggregateId || ""} ${r.lastError || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, processed: 0, failed: 0 };
    rows.forEach((r) => {
      if (c[r.status] != null) c[r.status]++;
    });
    return c;
  }, [rows]);

  return (
    <Stack
      spacing={3}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Outbox
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Durable after-commit side effects. Failed events stay here for review — click retry to requeue.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {health?.dispatcherPaused ? (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={async () => {
                try {
                  await resumeOutboxDispatcher();
                  load();
                } catch (err) {
                  setError(err?.message || "Resume failed");
                }
              }}
            >
              Resume dispatcher
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<PauseIcon />}
              onClick={async () => {
                if (!window.confirm("Pause the outbox dispatcher? Pending events will queue up until you resume. Use this for incident response when a downstream is known-broken.")) return;
                try {
                  await pauseOutboxDispatcher();
                  load();
                } catch (err) {
                  setError(err?.message || "Pause failed");
                }
              }}
            >
              Pause dispatcher
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {health?.dispatcherPaused ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <strong>Dispatcher paused on this instance.</strong> Pending events are queueing up.
          The pause is process-local — a pod restart resumes automatically. Resume manually as soon as the underlying issue is fixed.
        </Alert>
      ) : null}

      {health ? (
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Pending</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>{health.pending}</Typography>
                <Typography variant="caption" color="text.secondary">Awaiting dispatch</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Processed (24h)</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#16a34a", mt: 0.5 }}>
                  {health.processed24h}
                </Typography>
                <Typography variant="caption" color="text.secondary">Healthy throughput</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Failed (24h)</Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: (health.failed24h ?? 0) > 0 ? "#dc2626" : "#0f172a", mt: 0.5 }}
                >
                  {health.failed24h}
                </Typography>
                <Typography variant="caption" color="text.secondary">Dead-lettered events</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Oldest pending</Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mt: 0.5,
                    color: (health.oldestPendingAgeMs ?? 0) > 5 * 60 * 1000 ? "#f97316" : "#0f172a",
                  }}
                >
                  {(() => {
                    const ms = health.oldestPendingAgeMs;
                    if (ms == null) return "—";
                    if (ms < 0) return "scheduled";
                    if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
                    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
                    return `${Math.floor(ms / 3_600_000)}h`;
                  })()}
                </Typography>
                <Typography variant="caption" color="text.secondary">Older than 5m = check dispatcher</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {typeStats.length > 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              By type (24h)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              Worst-offending side-effect first. If everything is failing on the same type, that handler / destination is the problem.
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Pending</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Processed</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Failed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {typeStats.map((t) => (
                    <TableRow
                      key={t.type}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setFilter(t.type)}
                    >
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                          {t.type}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{t.pending}</TableCell>
                      <TableCell align="right" sx={{ color: t.processed > 0 ? "#16a34a" : "inherit" }}>
                        {t.processed}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: t.failed > 0 ? "#dc2626" : "inherit", fontWeight: t.failed > 0 ? 700 : 400 }}
                      >
                        {t.failed}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : null}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <ToggleButtonGroup
          size="small"
          value={statusFilter}
          exclusive
          onChange={(_, v) => setStatusFilter(v ?? "")}
        >
          <ToggleButton value="">All ({counts.all})</ToggleButton>
          <ToggleButton value="pending">Pending ({counts.pending})</ToggleButton>
          <ToggleButton value="processed">Processed ({counts.processed})</ToggleButton>
          <ToggleButton value="failed">Failed ({counts.failed})</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Filter by type / aggregate / error"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ flex: 1, minWidth: 280 }}
        />
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No matching events.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Aggregate</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Retries</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Next attempt</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last error</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelected(r)}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(r.createdAtMs)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {r.type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.aggregateType || "—"}
                      {r.aggregateId ? ` · ${r.aggregateId.slice(0, 8)}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.status}
                      sx={{
                        backgroundColor: STATUS_COLOR[r.status] || "#64748b",
                        color: "#fff",
                        fontWeight: 600,
                        height: 22,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{r.retryCount ?? 0}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {r.status === "pending" ? fmt(r.nextAttemptAtMs) : "—"}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                      {r.lastError || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {r.status !== "processed" ? (
                      <IconButton
                        size="small"
                        title="Requeue for immediate retry"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await retryOutboxEvent(r.id);
                            load();
                          } catch (err) {
                            setError(err?.message || "Retry failed");
                          }
                        }}
                      >
                        <ReplayIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Drawer
        anchor="right"
        open={!!selected}
        onClose={() => setSelected(null)}
        slotProps={{ paper: { sx: { width: { xs: "100%", sm: 520 } } } }}
      >
        {selected ? (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Event detail</Typography>
              <IconButton size="small" onClick={() => setSelected(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography sx={{ fontFamily: "monospace" }}>{selected.type}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  size="small"
                  label={selected.status}
                  sx={{
                    backgroundColor: STATUS_COLOR[selected.status] || "#64748b",
                    color: "#fff",
                    fontWeight: 600,
                    height: 22,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Retries: {selected.retryCount ?? 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Aggregate</Typography>
                <Typography variant="body2">
                  {selected.aggregateType || "—"}
                  {selected.aggregateId ? ` · ${selected.aggregateId}` : ""}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2">{fmt(selected.createdAtMs)}</Typography>
              </Box>
              {selected.status === "pending" ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">Next attempt</Typography>
                  <Typography variant="body2">{fmt(selected.nextAttemptAtMs)}</Typography>
                </Box>
              ) : null}
              {selected.processedAtMs ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">Processed</Typography>
                  <Typography variant="body2">{fmt(selected.processedAtMs)}</Typography>
                </Box>
              ) : null}
            </Stack>

            {selected.lastError ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Last error
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#fef2f2",
                    color: "#991b1b",
                    p: 1.5,
                    borderRadius: 1,
                    fontFamily: "monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {selected.lastError}
                </Box>
              </>
            ) : null}

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Payload {selected.payloadPreview && selected.payloadPreview.endsWith("…") ? "(truncated)" : ""}
            </Typography>
            <Box
              sx={{
                backgroundColor: "#0f172a",
                color: "#e2e8f0",
                p: 1.5,
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: 12,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 320,
                overflow: "auto",
              }}
            >
              {(() => {
                if (!selected.payloadPreview) return "(empty)";
                try {
                  return JSON.stringify(JSON.parse(selected.payloadPreview), null, 2);
                } catch {
                  return selected.payloadPreview;
                }
              })()}
            </Box>

            {selected.status !== "processed" ? (
              <Button
                fullWidth
                variant="contained"
                startIcon={<ReplayIcon />}
                sx={{ mt: 3, backgroundColor: "#1F4E78" }}
                onClick={async () => {
                  try {
                    await retryOutboxEvent(selected.id);
                    setSelected(null);
                    load();
                  } catch (err) {
                    setError(err?.message || "Retry failed");
                  }
                }}
              >
                Requeue for retry
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Drawer>
    </Stack>
  );
};

export default OutboxAdminPage;
